/**
 * NEXUS UI - Heatmap
 * Grid-based intensity visualization
 */

import React, { useState, useMemo } from 'react';
import type { BaseProps, NexusVariant } from './types';

interface HeatmapCell {
  x: number | string;
  y: number | string;
  value: number;
}

export interface HeatmapProps extends BaseProps {
  data: HeatmapCell[];
  xLabels?: string[];
  yLabels?: string[];
  variant?: NexusVariant;
  cellSize?: number;
  cellGap?: number;
  showValues?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  minValue?: number;
  maxValue?: number;
  colorSteps?: number;
}

const variantGradients: Record<NexusVariant, { low: string; mid: string; high: string }> = {
  cyan: { low: '#0d1117', mid: '#0d4f5c', high: '#00d4ff' },
  blue: { low: '#0d1117', mid: '#1a3a5c', high: '#3b82f6' },
  violet: { low: '#0d1117', mid: '#3b1a5c', high: '#a855f7' },
  green: { low: '#0d1117', mid: '#0d4a2a', high: '#00ff88' },
  amber: { low: '#0d1117', mid: '#5c3d0d', high: '#ffaa00' },
  red: { low: '#0d1117', mid: '#5c1a1a', high: '#ff3366' },
};

const interpolateColor = (color1: string, color2: string, factor: number): string => {
  const hex = (c: string) => parseInt(c, 16);
  const r1 = hex(color1.slice(1, 3));
  const g1 = hex(color1.slice(3, 5));
  const b1 = hex(color1.slice(5, 7));
  const r2 = hex(color2.slice(1, 3));
  const g2 = hex(color2.slice(3, 5));
  const b2 = hex(color2.slice(5, 7));

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const Heatmap: React.FC<HeatmapProps> = ({
  data,
  xLabels,
  yLabels,
  variant = 'cyan',
  cellSize = 32,
  cellGap = 2,
  showValues = false,
  showTooltip = true,
  showLegend = true,
  minValue,
  maxValue,
  colorSteps = 5,
  className = '',
  style,
}) => {
  const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null);

  const colors = variantGradients[variant];

  const { grid, xAxis, yAxis, min, max } = useMemo(() => {
    // Extract unique x and y values
    const xSet = new Set<string>();
    const ySet = new Set<string>();

    data.forEach(cell => {
      xSet.add(String(cell.x));
      ySet.add(String(cell.y));
    });

    const xAxisValues = xLabels || Array.from(xSet);
    const yAxisValues = yLabels || Array.from(ySet);

    // Calculate min/max
    const values = data.map(d => d.value);
    const minVal = minValue ?? Math.min(...values);
    const maxVal = maxValue ?? Math.max(...values);

    // Build grid
    const gridMap = new Map<string, HeatmapCell>();
    data.forEach(cell => {
      gridMap.set(`${cell.x}-${cell.y}`, cell);
    });

    const gridData = yAxisValues.map(y =>
      xAxisValues.map(x => gridMap.get(`${x}-${y}`) || { x, y, value: 0 })
    );

    return {
      grid: gridData,
      xAxis: xAxisValues,
      yAxis: yAxisValues,
      min: minVal,
      max: maxVal,
    };
  }, [data, xLabels, yLabels, minValue, maxValue]);

  const getColor = (value: number): string => {
    const normalized = Math.max(0, Math.min(1, (value - min) / (max - min || 1)));

    if (normalized < 0.5) {
      return interpolateColor(colors.low, colors.mid, normalized * 2);
    } else {
      return interpolateColor(colors.mid, colors.high, (normalized - 0.5) * 2);
    }
  };

  const legendSteps = Array.from({ length: colorSteps }, (_, i) => {
    const value = min + ((max - min) * i) / (colorSteps - 1);
    return { value, color: getColor(value) };
  });

  const labelPadding = 40;
  const totalWidth = xAxis.length * (cellSize + cellGap) + labelPadding;
  const totalHeight = yAxis.length * (cellSize + cellGap) + labelPadding;

  return (
    <div
      className={`nx-heatmap ${className}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        ...style,
      }}
    >
      <div style={{ position: 'relative' }}>
        <svg
          width={totalWidth}
          height={totalHeight}
          viewBox={`0 0 ${totalWidth} ${totalHeight}`}
          onMouseLeave={() => setHoveredCell(null)}
        >
          {/* Y-axis labels */}
          {yAxis.map((label, yi) => (
            <text
              key={`y-${yi}`}
              x={labelPadding - 8}
              y={labelPadding + yi * (cellSize + cellGap) + cellSize / 2 + 4}
              textAnchor="end"
              fill="var(--nx-text-tertiary)"
              fontSize="10"
              fontFamily="var(--nx-font-mono)"
            >
              {label}
            </text>
          ))}

          {/* X-axis labels */}
          {xAxis.map((label, xi) => (
            <text
              key={`x-${xi}`}
              x={labelPadding + xi * (cellSize + cellGap) + cellSize / 2}
              y={labelPadding - 8}
              textAnchor="middle"
              fill="var(--nx-text-tertiary)"
              fontSize="10"
              fontFamily="var(--nx-font-mono)"
            >
              {label}
            </text>
          ))}

          {/* Grid cells */}
          {grid.map((row, yi) =>
            row.map((cell, xi) => {
              const cellColor = getColor(cell.value);
              const isHovered = hoveredCell?.x === cell.x && hoveredCell?.y === cell.y;

              return (
                <g key={`${xi}-${yi}`}>
                  <rect
                    x={labelPadding + xi * (cellSize + cellGap)}
                    y={labelPadding + yi * (cellSize + cellGap)}
                    width={cellSize}
                    height={cellSize}
                    rx={2}
                    fill={cellColor}
                    stroke={isHovered ? colors.high : 'transparent'}
                    strokeWidth={2}
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-out',
                      filter: isHovered ? `drop-shadow(0 0 8px ${colors.high})` : 'none',
                    }}
                    onMouseEnter={() => setHoveredCell(cell)}
                  />
                  {showValues && (
                    <text
                      x={labelPadding + xi * (cellSize + cellGap) + cellSize / 2}
                      y={labelPadding + yi * (cellSize + cellGap) + cellSize / 2 + 4}
                      textAnchor="middle"
                      fill={cell.value > (max + min) / 2 ? 'var(--nx-void-deep)' : 'var(--nx-text-secondary)'}
                      fontSize="10"
                      fontFamily="var(--nx-font-mono)"
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      {cell.value}
                    </text>
                  )}
                </g>
              );
            })
          )}
        </svg>

        {/* Tooltip */}
        {showTooltip && hoveredCell && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'var(--nx-glass-dark)',
              border: `1px solid ${colors.high}`,
              borderRadius: '4px',
              padding: '8px 12px',
              boxShadow: `0 0 16px ${colors.high}40`,
              pointerEvents: 'none',
              zIndex: 10,
              whiteSpace: 'nowrap',
            }}
          >
            <div style={{ fontFamily: 'var(--nx-font-interface)', fontSize: '11px', color: 'var(--nx-text-tertiary)', marginBottom: '4px' }}>
              {hoveredCell.x} × {hoveredCell.y}
            </div>
            <div style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '16px', fontWeight: 600, color: colors.high }}>
              {hoveredCell.value}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: labelPadding }}>
          <span style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '10px', color: 'var(--nx-text-ghost)' }}>
            {min}
          </span>
          <div style={{ display: 'flex', height: '12px', borderRadius: '2px', overflow: 'hidden' }}>
            {legendSteps.map((step, i) => (
              <div
                key={i}
                style={{
                  width: '24px',
                  height: '100%',
                  background: step.color,
                }}
              />
            ))}
          </div>
          <span style={{ fontFamily: 'var(--nx-font-mono)', fontSize: '10px', color: 'var(--nx-text-ghost)' }}>
            {max}
          </span>
        </div>
      )}
    </div>
  );
};

export default Heatmap;
